import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Package, Trash, Upload, Download, QrCode, Camera, ImagePlus, Eye, MapPin, Phone, Mail, User as UserIcon, Copy } from "lucide-react";
import ShareTab from "@/pages/ShareTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProd = { name: "", description: "", price: 0, category: "Diagnostics",
                     image_url: "", stock: 100, is_featured: false };

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importPreview, setImportPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const resolveImg = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

  const uploadProductImage = async (file, opts = { fromCamera: false }) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is larger than 8 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((cur) => ({ ...(cur || emptyProd), image_url: data.url }));
      toast.success(opts.fromCamera ? "Photo captured & uploaded" : "Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const load = () => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data));
    axios.get(`${API}/admin/orders`).then((r) => setOrders(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!authLoading && user?.role === "admin") load();
  }, [authLoading, user]);

  if (authLoading) return null;
  if (!user || user.role !== "admin") {
    return (
      <div className="container-app px-6 py-20 text-center">
        <h1 className="font-heading text-3xl font-bold">Admin access required</h1>
        <p className="text-[#595959] mt-2">Sign in with an admin account.</p>
        <Link to="/login"><Button className="mt-6 rounded-full bg-[#638971] text-white">Sign in</Button></Link>
      </div>
    );
  }

  const save = async () => {
    try {
      if (editing.id) {
        await axios.put(`${API}/products/${editing.id}`, editing);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, editing);
        toast.success("Product created");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await axios.delete(`${API}/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggleSelect = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === products.length) setSelected(new Set());
    else setSelected(new Set(products.map((p) => p.id)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} selected product(s)? This cannot be undone.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/products/bulk-delete`, { ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} product(s)`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    }
  };

  // ---- CSV import ----
  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) return [];
    const splitLine = (line) => {
      const out = [];
      let cur = "", inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') inQ = !inQ;
        else if (c === "," && !inQ) { out.push(cur); cur = ""; }
        else cur += c;
      }
      out.push(cur);
      return out.map((s) => s.trim());
    };
    const headers = splitLine(lines[0]).map((h) => h.toLowerCase());
    const idx = (name) => headers.indexOf(name);
    const rows = [];
    for (let li = 1; li < lines.length; li++) {
      const cols = splitLine(lines[li]);
      const get = (n) => (idx(n) >= 0 ? cols[idx(n)] : "");
      const price = parseFloat(get("price")) || 0;
      const stock = parseInt(get("stock")) || 0;
      if (!get("name")) continue;
      rows.push({
        name: get("name"),
        description: get("description") || "",
        price,
        stock,
        category: get("category") || "Wellness",
        image_url: get("image_url") || get("image") || "",
        is_featured: /^(true|yes|1)$/i.test(get("is_featured") || get("featured") || ""),
      });
    }
    return rows;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      setImportText(text);
      setImportPreview(parseCSV(text));
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (importPreview.length === 0) {
      toast.error("Nothing to import — check your CSV");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/admin/products/import`, { products: importPreview });
      toast.success(`Imported ${data.inserted} product(s)`);
      setImportOpen(false);
      setImportText("");
      setImportPreview([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
  };

  const downloadTemplate = () => {
    const csv = `name,description,price,category,image_url,stock,is_featured
"Digital Wrist BP Monitor","Wrist-worn blood pressure monitor with irregular heartbeat detection.",39.99,Diagnostics,https://images.unsplash.com/photo-1580281657527-47f249e8f4df?w=800,50,true
"Cotton Swabs 500ct","Sterile cotton-tipped applicators.",4.99,First Aid,https://images.unsplash.com/photo-1585435557343-3b092031a831?w=800,200,false`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "products_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const fulfill = async (id) => {
    await axios.put(`${API}/admin/orders/${id}/fulfill`);
    toast.success("Marked as fulfilled");
    load();
  };

  const deleteOrder = async (id) => {
    if (!confirm("Delete this pending order? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/orders/${id}`);
      toast.success("Order deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const deleteAllPending = async () => {
    if (!confirm("Delete ALL pending/unpaid orders? This will remove every test order. This cannot be undone.")) return;
    try {
      const { data } = await axios.delete(`${API}/admin/orders/pending/all`);
      toast.success(`Deleted ${data.deleted} pending orders`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="container-app px-6 md:px-10 py-10">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A]">Admin</h1>
      </div>

      <Tabs defaultValue="products" className="mt-8">
        <TabsList className="bg-white border border-gray-100">
          <TabsTrigger data-testid="admin-tab-products" value="products">Products ({products.length})</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-orders" value="orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-share" value="share">
            <QrCode size={14} className="mr-1"/> Share
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
            <div className="flex items-center gap-2">
              {selected.size > 0 && (
                <>
                  <span data-testid="selected-count" className="text-sm text-[#595959]">{selected.size} selected</span>
                  <Button
                    data-testid="bulk-delete-btn"
                    onClick={bulkDelete}
                    variant="outline"
                    size="sm"
                    className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Trash size={14} className="mr-1"/> Delete selected
                  </Button>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="csv-template-btn"
                onClick={downloadTemplate}
                variant="outline"
                size="sm"
                className="rounded-full"
              >
                <Download size={14} className="mr-1"/> CSV template
              </Button>
              <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportText(""); setImportPreview([]); } }}>
                <DialogTrigger asChild>
                  <Button data-testid="import-csv-btn" variant="outline" size="sm" className="rounded-full">
                    <Upload size={14} className="mr-1"/> Import CSV
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-2xl">
                  <DialogHeader><DialogTitle>Import products from CSV</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="text-sm text-[#595959]">
                      Columns: <code>name, description, price, category, image_url, stock, is_featured</code>.
                      Not sure of the format? Click <button onClick={downloadTemplate} className="text-[#638971] underline">download template</button>.
                    </div>
                    <input
                      data-testid="csv-file-input"
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleFileUpload}
                      className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#638971] file:text-white file:font-semibold hover:file:bg-[#4F705B]"
                    />
                    <Textarea
                      data-testid="csv-text-input"
                      placeholder="Or paste CSV content here…"
                      value={importText}
                      onChange={(e) => { setImportText(e.target.value); setImportPreview(parseCSV(e.target.value)); }}
                      rows={6}
                      className="font-mono text-xs"
                    />
                    {importPreview.length > 0 && (
                      <div className="text-sm">
                        <div className="font-semibold text-[#2C4035] mb-2">
                          Preview — {importPreview.length} product(s) will be imported:
                        </div>
                        <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                          {importPreview.slice(0, 20).map((p, i) => (
                            <div key={i} className="flex justify-between px-3 py-2 text-xs">
                              <span className="truncate">{p.name} <span className="text-[#638971]">({p.category})</span></span>
                              <span className="font-semibold">${p.price.toFixed(2)}</span>
                            </div>
                          ))}
                          {importPreview.length > 20 && (
                            <div className="px-3 py-2 text-xs text-[#595959]">… and {importPreview.length - 20} more</div>
                          )}
                        </div>
                      </div>
                    )}
                    <Button
                      data-testid="confirm-import-btn"
                      onClick={handleImport}
                      disabled={importPreview.length === 0}
                      className="w-full rounded-full bg-[#638971] hover:bg-[#4F705B] text-white"
                    >
                      Import {importPreview.length > 0 ? `${importPreview.length} product(s)` : ""}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="admin-new-product-btn" onClick={() => setEditing({ ...emptyProd })}
                        className="rounded-full bg-[#638971] hover:bg-[#4F705B] text-white">
                  <Plus size={16} className="mr-1"/> New product
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-lg">
                <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} product</DialogTitle></DialogHeader>
                {editing && (
                  <div className="space-y-3">
                    <div><Label>Name</Label><Input data-testid="prod-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}/></div>
                    <div><Label>Description</Label><Textarea data-testid="prod-desc" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}/></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Price ($)</Label><Input data-testid="prod-price" type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })}/></div>
                      <div><Label>Stock</Label><Input data-testid="prod-stock" type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: parseInt(e.target.value) || 0 })}/></div>
                    </div>
                    <div><Label>Category</Label><Input data-testid="prod-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}/></div>
                    <div>
                      <Label>Product image</Label>
                      <div className="mt-1 space-y-2">
                        {editing.image_url && (
                          <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 bg-[#F6F5F2]">
                            <img
                              src={resolveImg(editing.image_url)}
                              alt="preview"
                              data-testid="prod-image-preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <label
                            data-testid="prod-image-camera"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#638971] hover:bg-[#4F705B] text-white text-sm font-medium cursor-pointer"
                          >
                            <Camera size={14}/> Snap photo
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              onChange={(e) => { uploadProductImage(e.target.files?.[0], { fromCamera: true }); e.target.value = ""; }}
                              disabled={uploading}
                            />
                          </label>
                          <label
                            data-testid="prod-image-file"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-gray-200 text-[#2C4035] text-sm font-medium cursor-pointer hover:bg-gray-50"
                          >
                            <ImagePlus size={14}/> Upload from device
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => { uploadProductImage(e.target.files?.[0]); e.target.value = ""; }}
                              disabled={uploading}
                            />
                          </label>
                        </div>
                        <Input
                          data-testid="prod-image"
                          value={editing.image_url}
                          onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                          placeholder="…or paste an image URL"
                          className="text-xs"
                        />
                        {uploading && <div className="text-xs text-[#638971]">Uploading…</div>}
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox data-testid="prod-featured" checked={editing.is_featured} onCheckedChange={(v) => setEditing({ ...editing, is_featured: !!v })}/>
                      Feature on homepage
                    </label>
                    <Button data-testid="prod-save" onClick={save} className="w-full rounded-full bg-[#638971] hover:bg-[#4F705B] text-white">Save</Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F5F2] text-[#595959] text-left">
                <tr>
                  <th className="p-3 w-10">
                    <Checkbox
                      data-testid="select-all-products"
                      checked={products.length > 0 && selected.size === products.length}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th className="p-3">Product</th><th className="p-3">Category</th>
                  <th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100">
                    <td className="p-3">
                      <Checkbox
                        data-testid={`select-${p.id}`}
                        checked={selected.has(p.id)}
                        onCheckedChange={() => toggleSelect(p.id)}
                      />
                    </td>
                    <td className="p-3 flex items-center gap-3">
                      <img src={resolveImg(p.image_url)} alt="" className="w-10 h-10 rounded object-cover"/>
                      <span className="font-medium">{p.name}</span>
                    </td>
                    <td className="p-3">{p.category}</td>
                    <td className="p-3">${p.price.toFixed(2)}</td>
                    <td className="p-3">{p.stock}</td>
                    <td className="p-3 text-right space-x-2">
                      <button data-testid={`edit-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }} className="text-[#638971] hover:text-[#4F705B]"><Edit size={16}/></button>
                      <button data-testid={`del-${p.id}`} onClick={() => del(p.id)} className="text-red-600 hover:text-red-700"><Trash2 size={16}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          {orders.some(o => o.payment_status !== "paid") && (
            <div className="flex justify-end mb-3">
              <Button
                data-testid="delete-all-pending-btn"
                onClick={deleteAllPending}
                variant="outline"
                size="sm"
                className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash size={14} className="mr-1"/> Delete all pending
              </Button>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {orders.length === 0 ? (
              <div className="p-10 text-center text-[#595959]">No orders yet.</div>
            ) : (
              <>
                {/* Mobile: tappable cards */}
                <div className="md:hidden divide-y divide-gray-100">
                  {orders.map((o) => (
                    <button
                      key={o.id}
                      data-testid={`order-card-${o.id}`}
                      onClick={() => setOrderDetail(o)}
                      className="w-full text-left p-4 hover:bg-[#F6F5F2]/60 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-semibold text-[#1A1A1A] truncate">
                            {o.customer_name || o.shipping_address?.name || "Customer"}
                          </div>
                          <div className="text-xs text-[#595959] truncate">{o.user_email || "Guest"}</div>
                          <div className="text-[11px] font-mono text-[#595959] mt-0.5">
                            #{o.session_id?.slice(-8)}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-heading font-bold text-[#2C4035]">${o.amount.toFixed(2)}</div>
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${o.payment_status === "paid" ? "bg-[#E8EDE9] text-[#2C4035]" : "bg-orange-50 text-[#E48967]"}`}>
                            {o.status}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-[#638971] font-semibold">
                        <Eye size={12}/> Tap to view address & items
                      </div>
                    </button>
                  ))}
                </div>

                {/* Desktop table */}
                <table className="hidden md:table w-full text-sm">
                <thead className="bg-[#F6F5F2] text-[#595959] text-left">
                  <tr>
                    <th className="p-3">Order</th><th className="p-3">Customer</th>
                    <th className="p-3">Total</th><th className="p-3">Status</th><th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr
                      key={o.id}
                      data-testid={`order-row-${o.id}`}
                      onClick={() => setOrderDetail(o)}
                      className="border-t border-gray-100 hover:bg-[#F6F5F2]/50 cursor-pointer"
                    >
                      <td className="p-3 font-mono text-xs">{o.session_id?.slice(-12)}</td>
                      <td className="p-3">
                        <div className="font-medium text-[#2C4035]">{o.customer_name || o.shipping_address?.name || "—"}</div>
                        <div className="text-xs text-[#595959]">{o.user_email || "Guest"}</div>
                      </td>
                      <td className="p-3 font-semibold">${o.amount.toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${o.payment_status === "paid" ? "bg-[#E8EDE9] text-[#2C4035]" : "bg-orange-50 text-[#E48967]"}`}>
                          {o.status}
                        </span>
                      </td>
                      <td className="p-3 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          data-testid={`view-order-${o.id}`}
                          onClick={() => setOrderDetail(o)}
                          className="text-[#638971] hover:text-[#4F705B] inline-flex items-center gap-1 text-sm"
                          title="View details"
                        >
                          <Eye size={14}/>
                        </button>
                        {o.payment_status === "paid" && o.status !== "fulfilled" && (
                          <Button data-testid={`fulfill-${o.id}`} onClick={() => fulfill(o.id)}
                                  size="sm" className="rounded-full bg-[#638971] hover:bg-[#4F705B] text-white">
                            <Package size={14} className="mr-1"/> Fulfill
                          </Button>
                        )}
                        {o.payment_status !== "paid" && (
                          <button
                            data-testid={`delete-order-${o.id}`}
                            onClick={() => deleteOrder(o.id)}
                            className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-sm"
                            title="Delete pending order"
                          >
                            <Trash2 size={14}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="share" className="mt-6">
          <ShareTab />
        </TabsContent>
      </Tabs>

      {/* Order details modal */}
      <Dialog open={!!orderDetail} onOpenChange={(v) => !v && setOrderDetail(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order details</DialogTitle>
          </DialogHeader>
          {orderDetail && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#595959] font-semibold">Order ID</div>
                  <div className="font-mono text-sm text-[#2C4035]">{orderDetail.session_id}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-wider text-[#595959] font-semibold">Placed</div>
                  <div className="text-sm text-[#2C4035]">
                    {orderDetail.created_at ? new Date(orderDetail.created_at).toLocaleString() : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#595959] font-semibold">Status</div>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${orderDetail.payment_status === "paid" ? "bg-[#E8EDE9] text-[#2C4035]" : "bg-orange-50 text-[#E48967]"}`}>
                    {orderDetail.status}
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold text-[#2C4035] flex items-center gap-2">
                    <UserIcon size={16}/> Customer
                  </div>
                  <button
                    data-testid="copy-customer-btn"
                    onClick={() => {
                      const a = orderDetail.shipping_address || {};
                      const text = [
                        orderDetail.customer_name || a.name,
                        a.street, a.street2,
                        [a.city, a.state, a.zip].filter(Boolean).join(", "),
                        a.country,
                        orderDetail.user_email,
                        orderDetail.customer_phone,
                      ].filter(Boolean).join("\n");
                      const fallback = () => {
                        try {
                          const ta = document.createElement("textarea");
                          ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
                          document.body.appendChild(ta); ta.select();
                          document.execCommand("copy"); document.body.removeChild(ta);
                          toast.success("Shipping label copied");
                        } catch { toast.error("Copy failed — please select and copy manually"); }
                      };
                      if (navigator.clipboard?.writeText) {
                        navigator.clipboard.writeText(text)
                          .then(() => toast.success("Shipping label copied"))
                          .catch(() => fallback());
                      } else { fallback(); }
                    }}
                    className="text-xs text-[#638971] hover:text-[#4F705B] inline-flex items-center gap-1"
                  >
                    <Copy size={12}/> Copy shipping label
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[#595959] text-xs">Name</div>
                    <div data-testid="order-customer-name" className="font-medium text-[#1A1A1A]">
                      {orderDetail.customer_name || orderDetail.shipping_address?.name || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#595959] text-xs flex items-center gap-1"><Mail size={12}/> Email</div>
                    <div className="font-medium text-[#1A1A1A] break-all">{orderDetail.user_email || "—"}</div>
                  </div>
                  {orderDetail.customer_phone && (
                    <div>
                      <div className="text-[#595959] text-xs flex items-center gap-1"><Phone size={12}/> Phone</div>
                      <div className="font-medium text-[#1A1A1A]">{orderDetail.customer_phone}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <div className="font-semibold text-[#2C4035] flex items-center gap-2 mb-3">
                  <MapPin size={16}/> Shipping address
                </div>
                {(() => {
                  const a = orderDetail.shipping_address || {};
                  const hasAddr = a.street || a.city || a.zip;
                  if (!hasAddr) return <div className="text-sm text-[#595959]">No shipping address collected.</div>;
                  return (
                    <div data-testid="order-shipping-address" className="text-sm leading-relaxed text-[#1A1A1A]">
                      {a.name && <div className="font-medium">{a.name}</div>}
                      {a.street && <div>{a.street}</div>}
                      {a.street2 && <div>{a.street2}</div>}
                      {(a.city || a.state || a.zip) && (
                        <div>{[a.city, a.state, a.zip].filter(Boolean).join(", ")}</div>
                      )}
                      {a.country && <div>{a.country}</div>}
                    </div>
                  );
                })()}
              </div>

              <div className="rounded-xl border border-gray-100 p-4">
                <div className="font-semibold text-[#2C4035] flex items-center gap-2 mb-3">
                  <Package size={16}/> Items ({orderDetail.items?.length || 0})
                </div>
                <div className="divide-y divide-gray-50">
                  {(orderDetail.items || []).map((it, i) => (
                    <div key={i} className="flex items-center gap-3 py-2">
                      <img src={resolveImg(it.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover bg-[#F6F5F2]"/>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[#1A1A1A]">{it.name}</div>
                        <div className="text-xs text-[#595959]">Qty {it.quantity} × ${it.price.toFixed(2)}</div>
                      </div>
                      <div className="text-sm font-semibold text-[#2C4035]">
                        ${(it.price * it.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-sm">
                  <div className="flex justify-between text-[#595959]">
                    <span>Subtotal</span>
                    <span>${(orderDetail.subtotal ?? orderDetail.amount ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#595959]">
                    <span>Shipping{orderDetail.shipping_label ? ` — ${orderDetail.shipping_label}` : ""}</span>
                    <span>{orderDetail.shipping ? `$${orderDetail.shipping.toFixed(2)}` : "—"}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold text-[#2C4035] pt-1">
                    <span>Total</span>
                    <span>${orderDetail.amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {orderDetail.payment_status === "paid" && orderDetail.status !== "fulfilled" && (
                <Button
                  data-testid="detail-fulfill-btn"
                  onClick={() => { fulfill(orderDetail.id); setOrderDetail(null); }}
                  className="w-full rounded-full bg-[#638971] hover:bg-[#4F705B] text-white h-11"
                >
                  <Package size={16} className="mr-2"/> Mark as fulfilled
                </Button>
              )}
              {orderDetail.payment_status !== "paid" && (
                <Button
                  data-testid="detail-delete-btn"
                  onClick={() => { deleteOrder(orderDetail.id); setOrderDetail(null); }}
                  variant="outline"
                  className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50 h-11"
                >
                  <Trash2 size={16} className="mr-2"/> Delete pending order
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
